import { TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { HeaderNavGroupItem } from './header-nav-group.model';
import { HeaderNavIconComponent } from './header-nav-icon.component';
import { HeaderNavIconId } from './header-nav-icons';

@Component({
  selector: 'li[app-header-nav-group]',
  standalone: true,
  imports: [TranslateModule, TitleCasePipe, HeaderNavIconComponent],
  templateUrl: './header-nav-group.component.html',
  styleUrl: './header-nav-group.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'link-tab nav-group',
    '[class.selected]': 'isSelected()',
    '[class.nav-group-open]': 'isOpen()',
    '(mouseenter)': 'onGroupEnter()',
    '(mouseleave)': 'onGroupLeave()',
    '(focusin)': 'onGroupEnter()',
    '(focusout)': 'onGroupLeave()',
  },
})
export class HeaderNavGroupComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly groupId = input.required<string>();
  readonly labelKey = input.required<string>();
  readonly iconId = input.required<HeaderNavIconId>();
  readonly items = input.required<HeaderNavGroupItem[]>();
  readonly currentRoute = input<string>('');

  readonly itemNavigate = output<HeaderNavGroupItem>();
  readonly itemHover = output<string>();

  readonly isOpen = signal(false);
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  readonly isSelected = computed(() =>
    this.items().some((item) => item.route === this.currentRoute())
  );

  constructor() {
    this.destroyRef.onDestroy(() => this.clearCloseTimer());
  }

  onTriggerClick(): void {
    this.clearCloseTimer();
    if (this.isOpen()) {
      return;
    }
    this.isOpen.set(true);
  }

  onGroupEnter(): void {
    this.clearCloseTimer();
    this.isOpen.set(true);
  }

  onGroupLeave(): void {
    this.clearCloseTimer();
    this.closeTimer = setTimeout(() => this.isOpen.set(false), 120);
  }

  onItemClick(item: HeaderNavGroupItem): void {
    this.clearCloseTimer();
    this.isOpen.set(false);
    this.itemNavigate.emit(item);
  }

  onItemHover(url: string): void {
    this.itemHover.emit(url);
  }

  isItemSelected(item: HeaderNavGroupItem): boolean {
    return item.route === this.currentRoute();
  }

  private clearCloseTimer(): void {
    if (this.closeTimer !== null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }
}
